import {moment} from "./moment";

export function formatRelativeTime(date: Date | string): string {
  return moment(date).fromNow();
}

export const formatDate = (date: Date | string, forceUtc: boolean = false) => {
  const mDate = forceUtc ? moment.utc(date) : moment(date);
  return mDate.format("MMM D, YYYY");
};

/** Mobile management list card header date — matches DAR (`30 MAR` + `HH:mm`). */
export function formatMobileCardDate(date: Date | string, forceUtc = false) {
  const m = forceUtc ? moment.utc(date) : moment(date);
  return m.format("DD MMM").toUpperCase();
}

export const formatTime = (date: Date | string) => {
  return moment(date).format("HH:mm");
};

/** 12-hour clock for DM list + bubble timestamps (e.g. `5:20 AM`). */
const CONVERSATION_TIME_FORMAT = "h:mm A";

/** Chat bubble — today: `5:20 AM`; older: date label + time (matches conversation list day rules). */
export function formatMessageTimestamp(date: Date | string): string {
  const m = moment(date);
  const now = moment();
  const time = m.format(CONVERSATION_TIME_FORMAT);

  if (m.isSame(now, "day")) {
    return time;
  }
  if (m.isSame(now.clone().subtract(1, "day"), "day")) {
    return `Yesterday · ${time}`;
  }
  if (m.isAfter(now.clone().subtract(7, "days"), "day")) {
    return `${m.format("dddd")} · ${time}`;
  }
  if (m.isSame(now, "year")) {
    return `${m.format("MMM D")} · ${time}`;
  }
  return `${m.format("MMM D, YYYY")} · ${time}`;
}

/** Conversation/inbox list — today: `10:30 AM`, yesterday: `Yesterday`, this week: weekday, else `MMM D`. */
export function formatConversationListDate(date: Date | string): string {
  const m = moment(date);
  const now = moment();

  if (m.isSame(now, "day")) {
    return m.format(CONVERSATION_TIME_FORMAT);
  }
  if (m.isSame(now.clone().subtract(1, "day"), "day")) {
    return "Yesterday";
  }
  if (m.isAfter(now.clone().subtract(7, "days"), "day")) {
    return m.format("dddd");
  }
  if (m.isSame(now, "year")) {
    return m.format("MMM D");
  }
  return m.format("MMM D, YYYY");
}

/** Safelisting coverage style — e.g. `Jun 25, 2026, 8:00 PM`. */
export const formatDateTime12 = (date: Date | string, forceUtc = false) => {
  const mDate = forceUtc ? moment.utc(date) : moment(date);
  return mDate.format("MMM D, YYYY, h:mm A");
};

/** Safelisting coverage / detail datetime labels (24h). */
export const SAFELISTING_COVERAGE_FORMATS = {
  full: "MMM D, YYYY, HH:mm",
  time: "HH:mm",
  compactWithDate: "MMM D, HH:mm",
  compactDate: "MMM D",
} as const;

export function getFormTime(date: Date = new Date()) {
  let hours = String(date.getHours());
  let minutes = String(date.getMinutes());

  if (hours.length === 1) {
    hours = `0${hours}`;
  }

  if (minutes.length === 1) {
    minutes = `0${minutes}`;
  }

  return `${hours}:${minutes}`;
}

export function getValidFilters(filters: Record<string, any>) {
  return Object.entries(filters)
    .filter(([_, value]) =>
      Array.isArray(value) ? value.length > 0 : typeof value === "boolean" ? true : Boolean(value),
    )
    .reduce(
      (acc, [key, value]) => {
        acc[key] = value;
        return acc;
      },
      {} as Record<string, any>,
    );
}

export function formatDecimal(value: number) {
  const hasDecimalPart = Math.floor(value) !== value;

  return hasDecimalPart ? +value.toFixed(2) : value;
}

export function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
